import { Link } from "@tanstack/react-router";
import { forwardRef, type ComponentProps } from "react";
import { useLocale, localizedPath, parsePath } from "@/lib/i18n/locale";

type AnyLinkProps = Omit<ComponentProps<typeof Link>, "to"> & {
  to?: string;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

/**
 * Drop-in <Link> replacement that auto-prefixes internal `to` paths
 * with the current URL locale (e.g. `/about` → `/vi/about` when on /vi).
 */
export const LocalizedLink = forwardRef<HTMLAnchorElement, AnyLinkProps>(
  function LocalizedLink({ to, ...rest }, ref) {
    const locale = useLocale();
    let finalTo: string | undefined = to;
    if (typeof to === "string" && to.startsWith("/")) {
      const { rest: clean } = parsePath(to);
      finalTo = localizedPath(clean, locale);
    }
    // Cast to any to bypass TanStack's strict typed-routes inference —
    // we navigate via runtime string paths in marketing nav.
    const LinkAny = Link as any;
    return <LinkAny ref={ref} to={finalTo} {...rest} />;
  }
);